// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'Modern Outdoor Living With Pergola Remodel in Santa Monica, CA 90402',
  description:
    'A modern outdoor living remodel in Santa Monica, CA featuring a custom pergola, integrated seating, clean architectural lines, and premium materials by Modern Build.',
  link: '/services/outdoor-living',
  height: 'h-[467px]',
  image:
    '/projects/modern-outdoor-living-with-pergola-remodel-in-santa-monica-90402/modern-outdoor-living-with-pergola-remodel-in-santa-monica-90402.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug = 'modern-outdoor-living-with-pergola-remodel-in-santa-monica-90402';
  const galleryPath = path.join(
    process.cwd(),
    'public',
    'projects',
    slug,
    'gallery'
  );

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
      title: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function ModernOutdoorLivingPergolaSantaMonica({
  galleryImages,
}) {
  const meta = metadata;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title}/>
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta
          name="keywords"
          content="modern outdoor living Santa Monica, pergola remodel 90402, outdoor living space remodel, backyard renovation, Modern Build"
        />
        <link
          rel="canonical"
          href="https://modernbuildremodel.com/projects/modern-outdoor-living-with-pergola-remodel-in-santa-monica-90402"
        />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta
          property="og:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/projects/modern-outdoor-living-with-pergola-remodel-in-santa-monica-90402"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:site_name"
          content="Modern Build Home Remodeling"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta.title}
        />
        <meta
          name="twitter:description"
          content={meta.description}
        />
        <meta
          name="twitter:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* Breadcrumb Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://modernbuildremodel.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Projects',
                  item: 'https://modernbuildremodel.com/projects',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name:
                    'Modern Outdoor Living With Pergola Remodel in Santa Monica CA 90402',
                  item:
                    'https://modernbuildremodel.com/projects/modern-outdoor-living-with-pergola-remodel-in-santa-monica-90402',
                },
              ],
            }),
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title:
                  'Modern Outdoor Living With Pergola Remodel in Santa Monica, CA 90402 | Modern Build',
                description:
                  'Explore a modern outdoor living remodel in Santa Monica featuring a custom pergola, clean lines, and a functional outdoor entertaining layout.',
                slug:
                  '/projects/modern-outdoor-living-with-pergola-remodel-in-santa-monica-90402',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero
        category="modernOutdoorLivingPergolaSantaMonica90402"
        page="page"
        hasContentH1={false}
      />

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="Modern Outdoor Living With Pergola Remodel in Santa Monica"
        description="This outdoor living remodel in Santa Monica, CA transformed an underutilized backyard into a refined, modern retreat. The project centered around a custom pergola structure designed to provide shade, architectural interest, and year round usability."
        extraText="The design balances open air comfort with clean, modern lines, creating a space ideal for relaxing, entertaining, and enjoying the Southern California climate."
        buttonText="Plan My Outdoor Living Project"
        buttonLink="/services/outdoor-living"
      />

      <Gallery images={galleryImages} />

      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/Cc-Zqmj5nwQ"
            serviceType="Outdoor Living Remodeling"
            location="Santa Monica, CA"
            description="Watch how this Santa Monica backyard was transformed into a modern outdoor living space with a custom pergola and intentional design details."
            serviceLink="/services/outdoor-living"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>

 <section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      Modern Outdoor Living With Pergola Remodel in Santa Monica, CA 90402
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This modern outdoor living remodel in Santa Monica, CA 90402 demonstrates how intentional design and skilled construction can extend a home’s usable space beyond its interior walls. The homeowners wanted an outdoor area that felt cohesive with the home’s architecture while offering comfort and versatility. Explore <a
        href="https://modernbuildremodel.com/outdoor-living/california/santa-monica/90402"
        className="text-primary underline"
      >
        outdoor living remodels in Santa Monica, CA 90402
      </a> for a closer look at what we can achieve.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      The design balances open-air comfort with clean, modern lines, creating a space ideal for relaxing, entertaining, and enjoying the Southern California climate. You can also view more examples of <a
        href="https://modernbuildremodel.com/outdoor-living/california/santa-monica/90402"
        className="text-primary underline"
      >
        Santa Monica outdoor living remodels
      </a> we’ve completed.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      For other outdoor remodeling projects across Southern California, see our <a
        href="https://modernbuildremodel.com/services/outdoor-living"
        className="text-primary underline"
      >
        main Outdoor Living services
      </a>.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Project Overview
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The existing backyard lacked structure and defined zones for seating or entertaining. While the location was ideal, the space did not encourage daily use or gatherings. Choosing a Santa Monica outdoor living remodel allowed us to create a functional and visually appealing environment.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Our goal was to introduce architectural elements that would organize the space, add shade, and elevate the overall look without overwhelming the property.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The custom pergola became the focal point of the design. Its clean lines and modern proportions define the seating area while providing partial shade and visual structure.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Materials were selected for durability and aesthetic consistency, ensuring the space remains low maintenance while aging gracefully. The layout promotes natural flow between indoor and outdoor areas.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Craftsmanship and Execution
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Our team handled every phase of construction, from site preparation to final detailing. Precision was critical to ensure the pergola structure was both visually striking and structurally sound.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      The result is an outdoor living space that feels intentional, refined, and built for long-term enjoyment.
    </p>

  </div>
</section>




      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
